import React, { ChangeEvent, ReactElement } from 'react';
import { ArticleDto, BatchDto, ClientDto, GoodsDto } from '../../../api/ClientApi';
import { Box, Grid2, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sr';
import { GoodsElementFormContainerBox } from '../../../common/ResponsiveStyle';
import { DimensionTypeDimensionSR, DimensionTypeSR } from '../../../common/Types';
import ArticleAutocomplete from '../../../common/components/autocomplete/ArticleAutocomplete';
import ClientAutocomplete from '../../../common/components/autocomplete/ClientAutocomplete';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';

type GoodElementProps = {
    good: GoodsDto;
    batch: BatchDto;
    index: number;
    removeGood(index: number): void;
    changeQuantity(index: number, quantity: number): void;
    selectArticle(index: number, articleId?: number): void;
    changePrice(index: number, price?: number): void;
    changeBatchArrivalTime(index: number, dateTime?: Date): void;
    changeBatchClient(index: number, batchClient?: ClientDto): void;
    changeUniqueBatchId(index: number, uniqueBatchId?: string): void;
    articles: ArticleDto[] | null;
    suppliers: ClientDto[] | null;
    isGoodsOnSite: boolean;
};

export default function GoodElement(props: GoodElementProps): ReactElement {
    const {
        good,
        batch,
        index,
        removeGood,
        changeQuantity,
        selectArticle,
        changePrice,
        changeBatchArrivalTime,
        changeBatchClient,
        changeUniqueBatchId,
        articles,
        suppliers,
        isGoodsOnSite,
    } = props;

    const handleRemoveGood = () => {
        if (!!removeGood && !!index) {
            removeGood(index);
        }
    };

    const handleGoodQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const type = e.target.type;

        if (!isNaN(+value) && !!changeQuantity && type === 'number') {
            changeQuantity(index, +value);
        }
    };

    const handleBatchPriceChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const type = e.target.type;

        if (!isNaN(+value) && !!changePrice && type === 'number') {
            changePrice(index, +value);
        }
    };

    const handleBatchArrivalTimeChange = (date: Dayjs | null) => {
        if (!!changeBatchArrivalTime) {
            changeBatchArrivalTime(index, date ? date.toDate() : undefined);
        }
    };

    const handleSelectSupplierId = (supplierId?: number) => {
        const supplier = suppliers?.find((s) => s.id === supplierId);

        changeBatchClient(index, supplier);
    };

    const handleUniqueBatchIdChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        if (!!changeUniqueBatchId) {
            changeUniqueBatchId(index, value);
        }
    };

    const handleSelectArticleId = (articleId?: number) => {
        selectArticle(index, articleId);
    };

    return (
        !!good && (
            <GoodsElementFormContainerBox>
                {isGoodsOnSite && (
                    <Box>
                        <Tooltip
                            title="Moguće dupliciranje. Ovaj artikal već postoji na datom skladištu od istog dobavljača, sa istim brojem fakture"
                            placement="top"
                        >
                            <Typography color="error" height={60} width={50} sx={{ pt: 1 }}>
                                <PriorityHighIcon sx={{ width: 50, height: 35 }} />
                            </Typography>
                        </Tooltip>
                    </Box>
                )}
                <Grid2 container spacing={2} sx={{ justifyContent: 'start', width: '100%' }}>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                        <Tooltip title={!!good.article ? good.article.title : ''}>
                            <div>
                                <ArticleAutocomplete
                                    isRequired
                                    articles={articles ?? []}
                                    selectArticle={handleSelectArticleId}
                                    selectedArticleId={good.article?.id}
                                />
                            </div>
                        </Tooltip>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                        <TextField
                            fullWidth
                            name="price"
                            label="Cena po kom."
                            type="number"
                            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                            value={!!batch?.purchasePricePerArticle ? batch?.purchasePricePerArticle : ''}
                            variant="outlined"
                            onChange={handleBatchPriceChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                        <TextField
                            error={!!good?.quantity && good?.quantity > 0 ? false : true}
                            required
                            fullWidth
                            name="quantity"
                            label={
                                good.article?.dimensionType ? DimensionTypeSR[good.article.dimensionType] : 'Količina'
                            }
                            slotProps={{
                                htmlInput: { inputMode: 'decimal' },
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {good.article?.dimensionType
                                                ? DimensionTypeDimensionSR[good.article.dimensionType]
                                                : 'kom.'}
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            type="number"
                            value={!!good?.quantity ? good?.quantity : ''}
                            variant="outlined"
                            onChange={handleGoodQuantityChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                        <Tooltip title={!!batch?.supplier && !!batch?.supplier.name ? batch.supplier.name : ''}>
                            <div>
                                <ClientAutocomplete
                                    clients={suppliers ?? []}
                                    selectClient={handleSelectSupplierId}
                                    selectedClientId={batch.supplier?.id}
                                />
                            </div>
                        </Tooltip>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                        <Tooltip title="Broj fakture" placement="top">
                            <TextField
                                error={!!batch?.uniqueBatchId && batch?.uniqueBatchId !== '' ? false : true}
                                required
                                fullWidth
                                name="uniqueBatchId"
                                label="Br. fakture"
                                value={!!batch?.uniqueBatchId ? batch?.uniqueBatchId : ''}
                                variant="outlined"
                                onChange={handleUniqueBatchIdChange}
                            />
                        </Tooltip>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sr">
                            <DateTimePicker
                                sx={{ width: '100%' }}
                                label="Ulazno vreme"
                                viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                }}
                                value={batch.batchArrivalTime ? dayjs(batch.batchArrivalTime) : null}
                                onChange={(value) => handleBatchArrivalTimeChange(value)}
                            />
                        </LocalizationProvider>
                    </Grid2>
                </Grid2>
                <Box style={{ flexGrow: 1 }} />
                <Tooltip title="Ukloni artikl" placement="left">
                    <IconButton sx={{ height: 40 }} onClick={handleRemoveGood}>
                        <DeleteIcon color="error" />
                    </IconButton>
                </Tooltip>
            </GoodsElementFormContainerBox>
        )
    );
}
